import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCreditCard, faChevronDown, faChevronUp, faCheckCircle, faBoxOpen, faArrowUp, faSpinner } from '@/fontawesome';
import type {EuroCent, SubscriptionPriceProduct} from '@/models';
import { clearChain } from '@/services/checkoutChain';
import { APIRoutes } from '@/services/api.routes';
import { useUser } from '@/contexts/UserContext';
import { showErrorToast } from '@/utils/toasts';
import { getUserFacingError } from '@/utils/errorMessages';
import { analytics, AnalyticsEvents } from '@/services/analytics';

export type BillingPeriod = '1m' | '6m' | '12m';

export type SubscriptionStatus =
  | 'NOT_SUBSCRIBED'               // = user can subscripe to this
  | 'SUBSCRIBED'                   // = user has already subscribed
  | 'CONTAINED'                    // = user has subscribed to a package containing all these features (probably ALL_*)
  | 'SUBSCRIBED_TO_HIGHER_PERIOD'  // = user has subscribed to a priceProduct with the same base-type but a longer billing period
  | 'SUBSCRIBED_TO_LOWER_PERIOD';  // = user has subscribed to a priceProduct with the same base-type but a shorter billing period

interface PricingCardProps {
  product: SubscriptionPriceProduct;
  billingPeriod: BillingPeriod;
  name: string;
  subtitle: string;
  shortDescription?: string;
  features: string[];
  idealFor?: string;
  popular?: boolean;
  subscriptionStatus: SubscriptionStatus;
  monthlyBasePrice?: EuroCent;
  highlightLongTerm?: boolean;
}

interface PricingData {
  basePricePerMonth: number;
  discountTotal: number;
  period: number;
  priceTotal: number;
  pricePerMonth: number;
  savingsTotal: number;
}

const calculatePricing = (price: EuroCent, period: BillingPeriod, monthlyBasePrice?: EuroCent): PricingData => {
  let periodMonths: number;

  switch (period) {
    case '6m':
      periodMonths = 6;
      break;
    case '12m':
      periodMonths = 12;
      break;
    default:
      periodMonths = 1;
  }

  const pricePerMonth = price / periodMonths;

  // Basispreis: Wenn monthlyBasePrice übergeben, diesen nutzen (= 1-Monats-Preis)
  const basePricePerMonth = monthlyBasePrice !== undefined
    ? monthlyBasePrice / 100
    : pricePerMonth / 100;

  // Ersparnis basierend auf dem echten Monatspreis berechnen
  const expectedFullPrice = monthlyBasePrice !== undefined
    ? (monthlyBasePrice * periodMonths)
    : price;
  const savings = expectedFullPrice - price;

  return {
    basePricePerMonth: basePricePerMonth,
    discountTotal:     0,
    priceTotal:        price/100,
    pricePerMonth:     pricePerMonth/100,
    period:            periodMonths,
    savingsTotal:      savings/100,
  };
};

interface ButtonConfig {
  text: string;
  disabled: boolean;
  variant: 'primary' | 'green' | 'blue' | 'amber' | 'gray';
  icon: typeof faCreditCard;
}

const getButtonConfig = (isProductInactive: boolean, subscriptionStatus: SubscriptionStatus): ButtonConfig => {
  if (isProductInactive) {
    return { text: 'Nicht verfügbar', disabled: true, variant: 'gray', icon: faCreditCard };
  }

  switch (subscriptionStatus) {
    case 'SUBSCRIBED':
      return { text: 'Bereits abonniert', disabled: true, variant: 'green', icon: faCheckCircle };
    case 'CONTAINED':
      return { text: 'Im Paket enthalten', disabled: true, variant: 'blue', icon: faBoxOpen };
    case 'SUBSCRIBED_TO_HIGHER_PERIOD':
      return { text: 'Längere Laufzeit aktiv', disabled: true, variant: 'gray', icon: faCheckCircle };
    case 'SUBSCRIBED_TO_LOWER_PERIOD':
      return { text: 'Upgrade', disabled: true, variant: 'amber', icon: faArrowUp };
    default:
      return { text: 'Jetzt buchen', disabled: false, variant: 'primary', icon: faCreditCard };
  }
};

const getButtonClasses = (variant: ButtonConfig['variant']): string => {
  switch (variant) {
    case 'green':
      return 'bg-green-600 dark:bg-green-700 cursor-not-allowed';
    case 'blue':
      return 'bg-blue-500 dark:bg-blue-600 cursor-not-allowed';
    case 'amber':
      return 'bg-amber-500 dark:bg-amber-600 cursor-not-allowed';
    case 'gray':
      return 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed';
    default:
      return 'bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 hover:shadow-xl hover:-translate-y-0.5 cursor-pointer';
  }
};

export default function PricingCard({
  product,
  billingPeriod,
  name,
  subtitle,
  shortDescription,
  features,
  idealFor,
  popular,
  subscriptionStatus,
  monthlyBasePrice,
  highlightLongTerm
}: PricingCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const { user } = useUser();

  const pricing = calculatePricing(product.price, billingPeriod, monthlyBasePrice);

  const handleToggle = () => {
    setIsExpanded(prev => !prev);
  };

  const handleCheckout = async () => {
    if (user === null) {
      showErrorToast('Du musst angemeldet sein, um ein Abo abzuschließen.');
      return;
    }

    setIsCheckoutLoading(true);

    try {
      // Verify product is still active
      const currentProduct = await APIRoutes.getSubscriptionProduct(product.type);
      if (!currentProduct.active) {
        showErrorToast('Dieses Produkt ist nicht mehr verfügbar.');
        setIsCheckoutLoading(false);
        return;
      }

      // Create subscription and get checkout URL
      const response = await APIRoutes.createSubscription(user.id, {
        items: [{ subscriptionType: product.type }]
      });

      // Track checkout started event before redirecting
      analytics.track(AnalyticsEvents.CHECKOUT_STARTED, {
        productId: product.type,
        productName: product.name,
        productType: product.type,
        priceAmount: product.price,
        priceCurrency: 'EUR',
      });

      // Eine liegen gebliebene Checkout-Kette (abgebrochener Mehrpaket-Link) gehört nicht zu
      // diesem Kauf – die Success-Seite erkennt ihn über die subscriptionId aus der URL.
      clearChain();

      // Redirect to Stripe checkout
      window.location.href = response.checkoutUrl;
    } catch (error) {
      console.error('Checkout error:', error);
      showErrorToast(getUserFacingError(error, 'Der Checkout konnte gerade nicht gestartet werden. Bitte versuche es in einem Moment noch einmal.'));
      setIsCheckoutLoading(false);
    }
  };

  const isProductInactive = !product.active;
  const buttonConfig = getButtonConfig(isProductInactive, subscriptionStatus);
  const isDisabled = buttonConfig.disabled;
  const isLongTermHighlight = highlightLongTerm === true && (billingPeriod === '6m' || billingPeriod === '12m') && popular !== true && !isDisabled;
  const longTermBadgeText = billingPeriod === '12m' ? 'BESTER PREIS · JAHRESABO' : 'BESTER PREIS · HALBJAHRESABO';

  return (
    <div
      className={`relative bg-white dark:bg-gray-800 rounded-xl p-6 border transition-all flex flex-col md:grid md:grid-rows-subgrid md:row-span-5 h-full ${
        isDisabled
          ? 'opacity-60 grayscale border-gray-300 dark:border-gray-600'
          : popular
            ? 'border-blue-500 shadow-lg shadow-blue-500/20 dark:shadow-blue-500/10 -translate-y-2 hover:shadow-xl hover:-translate-y-1'
            : isLongTermHighlight
              ? 'border-green-500 shadow-lg shadow-green-500/20 dark:shadow-green-500/10 -translate-y-1 hover:shadow-xl hover:-translate-y-2'
              : 'border-gray-200 dark:border-gray-700 shadow-md hover:shadow-xl hover:-translate-y-1'
      }`}
    >
      {/* Popular Badge */}
      {popular && !isDisabled && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-linear-to-r from-blue-600 to-blue-500 text-white text-xs font-semibold px-4 py-1 rounded-full shadow-lg whitespace-nowrap">
            AM BELIEBTESTEN
          </span>
        </div>
      )}

      {/* Long-Term Best-Price Badge */}
      {isLongTermHighlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-linear-to-r from-green-600 to-green-500 text-white text-xs font-semibold px-4 py-1 rounded-full shadow-lg whitespace-nowrap">
            {longTermBadgeText}
          </span>
        </div>
      )}

      {/* Row 1: Package Header */}
      <div className="mb-6 md:mb-0">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          {name}
        </h3>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {subtitle}
        </p>
        {shortDescription !== undefined && (
          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
            {shortDescription}
          </p>
        )}

        {/* Subscription Status Badge */}
        {subscriptionStatus === 'SUBSCRIBED' && (
          <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium rounded-full">
            <FontAwesomeIcon icon={faCheckCircle} className="w-3 h-3" />
            Aktives Abo
          </div>
        )}
        {subscriptionStatus === 'CONTAINED' && (
          <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs font-medium rounded-full">
            <FontAwesomeIcon icon={faBoxOpen} className="w-3 h-3" />
            Im Paket enthalten
          </div>
        )}
        {subscriptionStatus === 'SUBSCRIBED_TO_HIGHER_PERIOD' && (
          <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs font-medium rounded-full">
            <FontAwesomeIcon icon={faCheckCircle} className="w-3 h-3" />
            Längere Laufzeit aktiv
          </div>
        )}
      </div>

      {/* Row 2: Price Block */}
      <div className="mb-6 md:mb-0">
        <div className={`text-sm line-through mb-1 ${
          billingPeriod !== '1m'
            ? 'text-gray-500 dark:text-gray-400'
            : 'hidden md:block md:invisible'
        }`}>
          {pricing.basePricePerMonth.toFixed(2)} € / Monat
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold text-gray-900 dark:text-white">
            {pricing.pricePerMonth.toFixed(2)} €
          </span>
          <span className="text-gray-600 dark:text-gray-400">/ Monat</span>
        </div>
        <div className={`text-sm mt-1 ${
          pricing.savingsTotal > 0
            ? 'text-green-600 dark:text-green-400'
            : 'hidden md:block md:invisible'
        }`}>
          Du sparst {pricing.savingsTotal.toFixed(2)} €
        </div>
        <div className={`text-xs mt-2 ${
          billingPeriod !== '1m'
            ? 'text-gray-500 dark:text-gray-400'
            : 'hidden md:block md:invisible'
        }`}>
          Zum Preis von {pricing.priceTotal.toFixed(2)} € alle {pricing.period} Monate
        </div>
      </div>

      {/* Row 3: CTA Button + Mobile Toggle */}
      <div>
        <button
          onClick={handleCheckout}
          disabled={isDisabled || isCheckoutLoading}
          className={`w-full py-3 px-4 rounded-lg font-semibold text-white shadow-lg transition-all transform ${isCheckoutLoading ? 'bg-gray-400 dark:bg-gray-600 cursor-wait' : getButtonClasses(buttonConfig.variant)}`}
        >
          {isCheckoutLoading ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />
              Wird geladen...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={buttonConfig.icon} className="w-4 h-4 mr-2" />
              {buttonConfig.text}
            </>
          )}
        </button>
        <button
          onClick={handleToggle}
          className="md:hidden w-full mt-4 mb-6 py-2 px-4 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors cursor-pointer flex items-center justify-center gap-2"
        >
          <span>{isExpanded ? 'Details ausblenden' : 'Details anzeigen'}</span>
          <FontAwesomeIcon
            icon={isExpanded ? faChevronUp : faChevronDown}
            className="w-3 h-3"
          />
        </button>
      </div>

      {/* Row 4: Features */}
      <div className={`${isExpanded ? 'block' : 'hidden'} md:block`}>
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mb-4" />
        <div>
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
            Du bekommst
          </h4>
          <ul className="space-y-2">
            {features.map((feature, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                <FontAwesomeIcon
                  icon={faCheck}
                  className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 shrink-0"
                />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Row 5: Ideal für */}
      <div className={`${isExpanded ? 'block' : 'hidden'} md:block`}>
        {idealFor !== undefined && (
          <div className="pt-4 pb-4 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              Ideal für
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {idealFor}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
